import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Platform } from 'react-native';
import { useOnboardingStore, useAuthStore, useChatStore } from '@/store';
import { trackEvent } from '@/constants/utils/analytics';
import { isEligibleForOnboarding } from '@/constants/utils/onboarding';
import { ONBOARDING_VARIANT } from '@/constants/onboarding';

import OnboardingFlow from '@/components/Onboarding/OnboardingFlow';

export default function OnboardingOverlay() {
  const { isActive, actions } = useOnboardingStore();
  const { user, trackOnboarding, hasLoadedServerUser } = useAuthStore();
  const conversationCount = useChatStore(state => state.chats?.length || 0);

  if (Platform.OS === 'ios' || Platform.OS === 'android') return null;

  const stepsRef = useRef([]);
  const startedAtRef = useRef(null);
  // Set when the user completes or skips onboarding this session. Guards the
  // auto-start below against instantly re-launching the overlay in the window
  // before trackOnboarding's server response lands in local user.tracking.
  const dismissedThisSessionRef = useRef(false);

  const [forceMode, setForceMode] = useState(false);
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('forceOnboarding') === 'true') {
      setForceMode(true);
      actions.startOnboarding();
    }
  }, []);

  useEffect(() => {
    if (isActive && !startedAtRef.current) {
      startedAtRef.current = new Date().toISOString();
    }
  }, [isActive]);

  useEffect(() => {
    if (forceMode) return;

    // Onboarding is only for signed-in, fresh accounts. Logged-out visitors
    // browsing the public dashboard, existing users, and users who already
    // completed/skipped it should never see it.
    if (!isEligibleForOnboarding(user, conversationCount)) {
      if (isActive) {
        actions.stopOnboarding();
      }
      return;
    }

    // Fresh, eligible account with onboarding not running. The isActive flag
    // is persisted per-browser and gets switched off whenever a logged-out
    // visitor browses the public dashboard (the ineligible branch above) —
    // which is exactly the state a brand-new signup lands in. Nothing else
    // restarts onboarding, so without this explicit start a new user would
    // skip onboarding entirely. Wait for the server user so we decide on
    // fresh tracking/stats data rather than a stale persisted snapshot.
    if (!isActive && hasLoadedServerUser && !dismissedThisSessionRef.current) {
      actions.startOnboarding();
    }
  }, [user, isActive, conversationCount, forceMode, hasLoadedServerUser]);

  useEffect(() => {
    if (isActive) {
      trackEvent('onboarding', 'variant_shown', ONBOARDING_VARIANT);
    }
  }, [isActive]);

  const recordStep = useCallback((stepIndex) => {
    const now = Date.now();
    const steps = stepsRef.current;
    if (steps.length > 0) {
      const prev = steps[steps.length - 1];
      if (!prev.duration) {
        prev.duration = now - new Date(prev.viewedAt).getTime();
      }
    }
    if (!steps.find(s => s.step === stepIndex)) {
      steps.push({ step: stepIndex, viewedAt: new Date(now).toISOString() });
    }
  }, []);

  const buildTrackingPayload = useCallback(() => {
    const steps = stepsRef.current;
    if (steps.length > 0) {
      const last = steps[steps.length - 1];
      if (!last.duration) {
        last.duration = Date.now() - new Date(last.viewedAt).getTime();
      }
    }
    return {
      variant: ONBOARDING_VARIANT,
      startedAt: startedAtRef.current,
      steps,
    };
  }, []);

  const handleComplete = useCallback(() => {
    dismissedThisSessionRef.current = true;
    actions.stopOnboarding();
    trackOnboarding(buildTrackingPayload());
    if (!forceMode) {
      trackEvent('onboarding', 'completed', ONBOARDING_VARIANT);
    }
  }, [actions, trackOnboarding, forceMode, buildTrackingPayload]);

  if (!isActive) return null;
  if (!user && !forceMode) return null;

  return (
    <OnboardingFlow
      onComplete={handleComplete}
      forceMode={forceMode}
      userName={user?.name}
      recordStep={recordStep}
    />
  );
}
